import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin, Button, Tooltip } from 'antd'
import { cloneDeep, set } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createSaveImage,
  isReadOnly,
} from 'helpers/formViews'
import { getSupplierIntegrationFormOptions } from 'helpers/supplierIntegration'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import FormButtons from 'elements/FormButtons'
import Select, { Option } from 'elements/Select'
import InputImage from 'elements/InputImage'
import Drawer from 'elements/Drawer'
import FileUpload from 'elements/FileUpload'
import { Row, Col } from 'elements/Grid'

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.saveImage = createSaveImage(this, 'Supplier', 'ObjectImage')
  }

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.supplierIntegrationId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props
              .getItem(`${itemId}?isFromMaster=${this.props.linkTargetRecord?.isFromMaster}`)
              .catch((error) => {
                this.props.onCancel?.()
                throw error
              })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const [image] = await Promise.all([
        item.id
          ? this.props
              .getDocumentContents({
                request: {
                  domainObjectId: item.id,
                  domainObjectType: 'Supplier',
                  documentType: item.isFromMaster ? 'CompanyLogo' : 'ObjectImage',
                  documentName: item.isFromMaster ? item.logoFile : item.name,
                },
              })
              .catch(() => {})
          : Promise.resolve(null),
      ])

      this.setState({
        item: responses[0].value.data,
        fieldSettings: responses[1].value.data.fieldSettings,
        image: image?.value?.data,
        imageOriginal: image?.value?.data,
      })
    } catch (error) {
      showError({ error })
    }
  }

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await this.saveImage(saved.value.data.id, item.name)

    return saved
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )

  handleFileUpload = (fileList = []) => {
    if (fileList[0]) {
      this.setState({
        image: {
          contents: fileList[0].fileContents,
          fileType: fileList[0].fileType,
        },
        fileUploadDrawerVisible: false,
      })
    } else {
      this.setState({ fileUploadDrawerVisible: false })
    }
  }

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || item.isFromMaster

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
                </Form.Item>
                <Form.Item label={createLabel('supplierIntegrationType')}>
                  {createFieldDecorator('supplierIntegrationType')(
                    <Select
                      onChange={(value) => this.setItemValue('supplierIntegrationType', value)}
                      allowClear={false}
                      readOnly={readOnly}
                    >
                      {Object.entries(
                        getSupplierIntegrationFormOptions(
                          this.props.customer.generalSettings.enableExternalItemStatusRequest
                        )
                      ).map(([key, value]) => (
                        <Option key={key} value={key}>
                          {t(value)}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
                <Form.Item label={createLabel('url')}>
                  {createFieldDecorator('url')(<Input type="url" readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={24}>
                <Form.Item label={createLabel('fromDomain')}>
                  {createFieldDecorator('fromDomain')(<Input readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('domain')}>
                  {createFieldDecorator('domain')(<Input readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('senderDomain')}>
                  {createFieldDecorator('senderDomain')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item label={createLabel('fromIdentity')}>
                  {createFieldDecorator('fromIdentity')(<Input readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('identity')}>
                  {createFieldDecorator('identity')(<Input readOnly={readOnly} />)}
                </Form.Item>
                <Form.Item label={createLabel('senderIdentity')}>
                  {createFieldDecorator('senderIdentity')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={24}>
                <Form.Item label={createLabel('sharedSecret')}>
                  {createFieldDecorator('sharedSecret')(<Input.Password readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(<Input.TextArea rows={3} readOnly={readOnly} />)}
                </Form.Item>
              </Col>
            </Row>
            {item.supplierIntegrationType === 'PunchoutRecipient' && (
              <Row>
                <Col md={12} xs={24}>
                  <InputImage
                    label={t('logo')}
                    onUpload={() => this.setState({ fileUploadDrawerVisible: true })}
                    onDelete={() => this.setState({ image: null })}
                    value={this.state.image}
                    readOnly={readOnly}
                  />
                </Col>
              </Row>
            )}
          </div>
          <div className="form-buttons-container">
            {readOnly ? (
              <Tooltip title={t('cannotEditLegacyRecord')}>
                <Button onClick={this.props.onCancel}>{t('close')}</Button>
              </Tooltip>
            ) : (
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            )}
          </div>
        </Form>
        <Drawer
          title={t('selectFile')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
          />
        </Drawer>
      </>
    )
  }
}

export default Form.create()(FormView)
